/**
 * 金额转化规则
 * wiki: http://wiki.al.com/pages/viewpage.action?pageId=34899209
 */
import _ from "lodash";

import { CountryCode, CurrencyCode } from "../common/enums";

import {
  formatSeparatorDotAndPointComma,
  formatSeparatorCommaAndPointDot,
  priceShortID,
  priceShortVN,
  priceShortPH,
  priceShortRM,
  formatEuroPrice,
  formatSecondEuroPrice,
  formatPLNPrice,
} from "./util";

const formatEuroRule = {
  price: formatEuroPrice,
  shortprice: formatEuroPrice,
  // 简写时是否隐藏货币符号
  shortpriceHideCurrencyCode: false,
  // 货币符号显示位置
  posAtRight: true,
  // 货币符号和金额之前是否需要空格
  needSpace: true,
  separator: ".",
  pointStr: ",",
};

const formatSecondEuroRule = {
  price: formatSecondEuroPrice,
  shortprice: formatSecondEuroPrice,
  // 简写时是否隐藏货币符号
  shortpriceHideCurrencyCode: false,
  // 货币符号显示位置
  posAtRight: true,
  // 货币符号和金额之前是否需要空格
  needSpace: true,
  separator: " ",
  pointStr: ",",
};

// 各国家金额转化规则
export const formatRules = {
  // 中文
  [CountryCode.CHN]: undefined,
  // 印尼
  [CountryCode.ID]: {
    // 正常
    price: formatSeparatorDotAndPointComma,
    // 简写
    shortprice: priceShortID,
    // 简写时是否隐藏货币符号
    shortpriceHideCurrencyCode: false,
    // 货币符号显示位置
    posAtRight: false,
    needSpace: false,
    separator: ".",
    pointStr: ",",
  },
  // 越南
  [CountryCode.VN]: {
    price: formatSeparatorDotAndPointComma,
    shortprice: priceShortVN,
    // 简写时是否隐藏货币符号
    shortpriceHideCurrencyCode: true,
    // 货币符号显示位置
    posAtRight: true,
    needSpace: false,
    separator: ".",
    pointStr: ",",
  },
  // 马来西亚
  [CountryCode.MY]: {
    price: formatSeparatorCommaAndPointDot,
    shortprice: priceShortRM,
    // 简写时是否隐藏货币符号
    shortpriceHideCurrencyCode: false,
    // 货币符号显示位置
    posAtRight: false,
    needSpace: false,
    separator: ",",
    pointStr: ".",
  },
  // 菲律宾
  [CountryCode.PH]: {
    price: formatSeparatorCommaAndPointDot,
    shortprice: priceShortPH,
    // 简写时是否隐藏货币符号
    shortpriceHideCurrencyCode: false,
    // 货币符号显示位置
    posAtRight: false,
    needSpace: false,
    separator: ",",
    pointStr: ".",
  },
  // 德国
  [CountryCode.DE]: formatEuroRule,
  // 西班牙
  [CountryCode.ES]: formatEuroRule,
  // 法国
  [CountryCode.FR]: formatSecondEuroRule,
  // 意大利
  [CountryCode.IT]: formatEuroRule,
  // 比利时
  [CountryCode.BE]: formatEuroRule,
  // 波兰
  [CountryCode.PL]: {
    price: formatPLNPrice,
    shortprice: formatPLNPrice,
    // 简写时是否隐藏货币符号
    shortpriceHideCurrencyCode: false,
    // 货币符号显示位置
    posAtRight: true,
    // 货币符号和金额之前是否需要空格
    needSpace: true,
    separator: " ",
    pointStr: ",",
  },
};

/**
 * @name 转化金额方法
 * @param {Object} options
 * 参数描述
 * - {string} currencyCode 货币符号
 * - {number|string} value 金额
 * - {string} format 默认"shortprice" "shortprice"(缩写) | "price"(全显)
 * - {boolean} needCurrencyCode 是否需要显示货币符号 默认 true
 */
export function formatFunc({
  countryCode,
  value,
  format = "shortprice",
  needCurrencyCode,
}: {
  countryCode: CountryCode;
  value?: string | number;
  format: "shortprice" | "price";
  needCurrencyCode?: boolean;
}): string | number {
  if (!value && value !== 0) return "";
  if (isNaN(Number(value))) {
    throw new Error("price must be a number: " + value);
  }
  const rule = formatRules[countryCode];
  if (rule && rule[format]) {
    value = Number(value) || 0;

    // 越南的货币符号放在金额后面，且只有全价显示的时候需要
    const showPrice = rule[format](Math.abs(value));
    let currencyCode = needCurrencyCode ? CurrencyCode[countryCode] : "";
    if (format === "shortprice" && rule.shortpriceHideCurrencyCode) {
      currencyCode = "";
    }
    if (rule.posAtRight) {
      return `${value < 0 ? "-" : ""}${showPrice}${rule.needSpace ? " " : ""}${currencyCode}`;
    } else {
      return `${currencyCode}${rule.needSpace ? " " : ""}${value < 0 ? "-" : ""}${showPrice}`;
    }
  }
  return value;
}

/**
 * @name 转化金额方法
 * @param {Object} options
 * 参数描述
 * - {string} currencyCode 货币符号
 * - {number|string} value 金额
 * - {string} format 默认"shortprice" "shortprice"(缩写) | "price"(全显)
 * - {boolean} needCurrencyCode 是否需要显示货币符号 默认 true
 */
export function format2objectFunc({
  countryCode,
  value,
  format,
}: {
  countryCode: CountryCode;
  value?: string | number;
  format: "shortprice" | "price";
}): {
  currencyCode: CurrencyCode;
  value?: string | number;
  unit?: string;
  posAtRight?: boolean;
  separator?: string;
  pointStr?: string;
} {
  if (!value && value !== 0)
    return {
      currencyCode: CurrencyCode[countryCode],
    };
  if (isNaN(Number(value))) {
    throw new Error("price must be a number: " + value);
  }
  const rule = formatRules[countryCode];
  if (rule && rule[format]) {
    value = Number(value) || 0;

    const showPrice = rule[format](Math.abs(value));
    // 使用正则匹配格式化后的金额和缩写单位
    const matchResult = showPrice.match(/([\d\s\\.,]*)([a-zA-Z]*)/);
    return {
      currencyCode: CurrencyCode[countryCode],
      value: (value < 0 ? "-" : "") + _.get(matchResult, "1", "").trim(),
      unit: _.get(matchResult, "2", ""),
      posAtRight: rule.posAtRight,
      separator: rule.separator,
      pointStr: rule.pointStr,
    };
  }
  return {
    currencyCode: CurrencyCode[countryCode],
    value,
  };
}
